import type {
  ConsoleDatabase,
  ConsoleTable,
  ExecuteRequest,
  QueryResult,
  SaveQueryInput,
  SavedQuery,
  SavedQueryStore,
} from '@speles7172/sql-console';
import { apiClient } from './client';

/**
 * The SQL Client endpoints, shaped by `SqlConsoleTransport`.
 *
 * Thin on purpose. The console builds the requests and reads the results; this
 * file only carries them, and the types come from the package rather than being
 * restated here — a second declaration of `QueryResult` is a second thing to
 * keep in step with the endpoint.
 */

export type {
  ConsoleDatabase,
  ConsoleTable,
  ExecuteRequest,
  QueryResult,
  SaveQueryInput,
  SavedQuery,
};

/** The picker: the live database, plus the backups where they are configured. */
export async function listDatabases(): Promise<ConsoleDatabase[]> {
  const { data } = await apiClient.get<ConsoleDatabase[]>('/admin/sql/databases');
  return data;
}

export async function listTables(database?: string): Promise<ConsoleTable[]> {
  const { data } = await apiClient.get<ConsoleTable[]>('/admin/sql/tables', {
    params: database === undefined ? undefined : { database },
  });
  return data;
}

export async function execute(request: ExecuteRequest): Promise<QueryResult> {
  const { data } = await apiClient.post<QueryResult>('/admin/sql/query', request);
  return data;
}

/** The team's saved queries, most recently changed first. */
export async function listSavedQueries(): Promise<SavedQuery[]> {
  const { data } = await apiClient.get<SavedQuery[]>('/admin/sql/saved-queries');
  return data;
}

/** Create a saved query, or overwrite the one named by `input.id`. */
export async function saveSavedQuery(input: SaveQueryInput): Promise<SavedQuery> {
  const { data } = await apiClient.post<SavedQuery>('/admin/sql/saved-queries', input);
  return data;
}

export async function deleteSavedQuery(id: string): Promise<void> {
  await apiClient.delete(`/admin/sql/saved-queries/${encodeURIComponent(id)}`);
}

/**
 * The SQL client's saved queries: one library on the server that every
 * administrator shares, rather than the package's per-browser default.
 *
 * Module-level on purpose. The console keys its loading on the store's
 * identity, so a store built during render is a reload on every render; and
 * the console hides saving altogether when it is given no store, which is how
 * the screen came to offer none.

 */
export const savedQueryStore: SavedQueryStore = {
  list: () => listSavedQueries(),
  save: (input) => saveSavedQuery(input),
  remove: (id) => deleteSavedQuery(id),
};
